import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import PropTypes from 'prop-types';
import { styled, alpha } from '@mui/material/styles';
import toast from 'react-hot-toast';
import {
  Toolbar,
  Tooltip,
  IconButton,
  Typography,
  OutlinedInput,
  InputAdornment,
  FormControlLabel,
  Switch,
  Grid,
  Box,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Label from '../../../components/label';
import { DeleteWorker } from '../../../services/workerService';
import Iconify from '../../../components/iconify';
import { getClothesByBarcode } from '../../../services/clothesService';
import YesNo from '../../../pages/YesNo';

const StyledRoot = styled(Toolbar)(({ theme }) => ({
  height: 96,
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 1, 0, 3),
}));

const StyledSearch = styled(OutlinedInput)(({ theme }) => ({
  transition: theme.transitions.create(['box-shadow', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  '&.Mui-focused': {
    boxShadow: theme.customShadows.z8,
  },
  '& fieldset': {
    borderWidth: `1px !important`,
    borderColor: `${alpha(theme.palette.grey[500], 0.32)} !important`,
  },
}));

export default function ResidentListToolbar({
  numSelected,
  filterName,
  onFilterName,
  filterBarCodeClothes,
  onFilterBarCodeClothes,
  filterActive,
  onFilterActive,
  filterAlive,
  onFilterAlive,
  selected,
  userList,
  setUserList,
  setSelected,
}) {

  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const handleFilterByName = (e) => onFilterName(e.target.value); // Il est appelé par le parent
  const [searching, setSearching] = useState(false);
  const [localClothesBarcode, setLocalClothesBarcode] = useState(''); // État local pour la saisie

  // Synchroniser l'état local avec l'état parent quand le filtre est vidé
  useEffect(() => {
    if (!filterBarCodeClothes) {
      setLocalClothesBarcode('');
    }
  }, [filterBarCodeClothes]);

  const handleClothesBarcodeChange = (e) => {
    const value = e.target.value;
    setLocalClothesBarcode(value);
    // Vider le champ de recherche résident si on tape dans le code-barre vêtement
    if (value && filterName) {
      onFilterName({ target: { value: '' } });
    }
  };

  const handleDeleteClick = () => {
    setOpen(true);
  };

  const handleConfirmDelete = async () => {

    selected.forEach(async (id) => {
      const resident = userList.find((user) => user.id === id);
      if (await DeleteWorker(id)) {
        const updatedUserList = userList.filter((user) => user.id !== id);
        setUserList(updatedUserList);
        toast.success(`Le résident ${resident.firstname} ${resident.lastname} a été supprimé avec succès`);
      } else {
        toast.error(`Une erreur est survenue lors de la suppression du résident ${resident.firstname} ${resident.lastname}`)
      }
    });

    setSelected([]);
    setOpen(false);
  };

  const handleCancelDelete = () => {
    setSelected([]);
    setOpen(false);
  };

  const handleBarcodeSearch = async (e) => {
    e.preventDefault();
    const trimmed = (localClothesBarcode || '').trim();
    if (!trimmed) return;
    try {
      setSearching(true);
      const dto = await getClothesByBarcode(trimmed);
      // console.log('Recherche vêtement avec code-barre:', trimmed); // Debug: F12 dans le navigateur
      // console.log('Réponse API vêtement:', dto); // Debug: F12 dans le navigateur
      if (dto && dto.workerGuid && dto.workerGuid.trim() !== '') {
        // console.log('WorkerGuid trouvé:', dto.workerGuid); // Debug: F12 dans le navigateur
        // Filtrer la table par WorkerGuid au lieu de naviguer
        onFilterBarCodeClothes(dto.workerGuid);
        // Vider le champ de recherche résident
        onFilterName({ target: { value: '' } });
        toast.success('Vêtement trouvé, filtrage du tableau');
      } else {
        // console.log('Aucun workerGuid dans la réponse'); // Debug: F12 dans le navigateur
        toast.error('Aucun vêtement trouvé avec ce code-barre');
      }
    } catch (err) {
      // console.error('Erreur lors de la recherche vêtement:', err); // Debug: F12 dans le navigateur
      toast.error('Aucun vêtement trouvé avec ce code-barre');
    } finally {
      setSearching(false);
    }
  };

  return (
    <>
      <YesNo
        message={'Êtes-vous sûr de vouloir supprimer les éléments sélectionnés ?'}
        open={open}
        setOpen={setOpen}
        setResult={(result) => {
          if (result === 1) {
            handleConfirmDelete();
          } else {
            handleCancelDelete();
          }
        }}
      />
      <StyledRoot
        sx={{
          ...(numSelected > 0 && {
            color: 'primary.main',
            bgcolor: 'primary.lighter',
          }),
        }}
      >
        {numSelected > 0 ? (
          <Typography component="div" variant="subtitle1">
            {numSelected} résident(s) sélectionné(s)
          </Typography>
        ) : (<>
          <Grid>
              <FormControlLabel
                              checked={filterActive}
                              onChange={onFilterActive}
                              control={<Switch />}
                              label={<Label color={filterActive ? 'success' : 'error'}>{filterActive ? t('resident-list-active') : t('resident-list-inactive')}</Label>}
              />
              <FormControlLabel
                              checked={filterAlive}
                              onChange={onFilterAlive}
                              control={<Switch />}
                              label={<Label color={filterAlive ? 'success' : 'error'}>{filterAlive ? t('resident-list-customer') : t('resident-list-dead')}</Label>}
              />
          </Grid>
              <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                
                <StyledSearch // Recherche résident (nom, prénom, code-barre résident)
                  sx={{ flex: 1 }}
                  value={filterName}
                  onChange={onFilterName}
                  placeholder={t('resident-list-search-resident')}
                  startAdornment={
                    <InputAdornment position="start">
                      <Iconify icon="eva:person-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                    </InputAdornment>
                  }
                />
                <form onSubmit={handleBarcodeSearch} style={{ display: 'flex' }}>
                  <StyledSearch // Code-barre vêtement (WorkerGuidByClothes)
                    sx={{ width: 280 }}
                    value={localClothesBarcode}
                    onChange={handleClothesBarcodeChange}
                    placeholder={t('resident-list-search-barcode-clothes') || 'Code-barre vêtement'}
                    startAdornment={<InputAdornment position="start">#</InputAdornment>}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton type="submit" disabled={searching}>
                          <Iconify icon="eva:arrow-forward-outline" />
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </form>
              </Box>
            </>
        )}

        {numSelected > 0 && (
          <Tooltip title="Delete">
            <IconButton onClick={handleDeleteClick}>
              <Iconify icon="eva:trash-2-fill" />
            </IconButton>
          </Tooltip>
        )}
      </StyledRoot>
    </>
  );
}
